import { useEffect, useRef, RefObject } from 'react';

/**
 * Enquanto um modal está aberto, a página atrás não rola.
 *
 * ── O que estava acontecendo ──────────────────────────────────────────────
 *
 * Nenhum dos seis modais travava nada. Com a boas-vindas aberta, girar a roda
 * rolava o app inteiro por baixo dela — e a medição confirmou: `overflow` do
 * `body` em `visible` e `scrollY` em 261 com o modal no ar.
 *
 * No desktop isso é chato. No celular é pior, porque não existe "sair do
 * modal com o dedo": a pessoa rola dentro da caixa, o gesto vaza para a
 * página, e quando ela fecha está num lugar que não escolheu.
 *
 * ── Por que `position: fixed` e não `overflow: hidden` ────────────────────
 *
 * `overflow: hidden` no `body` não segura o Safari do iPhone — ele continua
 * rolando o documento. `position: fixed` segura em toda parte, ao custo de
 * ter que guardar e devolver a posição na mão, senão a página salta para o
 * topo ao abrir e fica lá ao fechar.
 *
 * ── Por que um contador ───────────────────────────────────────────────────
 *
 * A boas-vindas abre o tutorial POR CIMA dela. Com um booleano, o primeiro a
 * desmontar destravaria a página com o segundo ainda aberto. O contador só
 * devolve o scroll quando o último sai.
 *
 * ── Por que compensar a barra ─────────────────────────────────────────────
 *
 * Tirar a rolagem tira a barra, e a página alarga alguns pixels no instante
 * em que o modal aparece: todo o conteúdo dá um pulinho lateral atrás da
 * caixa. O `padding-right` do tamanho da barra ocupa o lugar dela.
 */

let abertos = 0;
let scrollGuardado = 0;
/*
 * A devolução que ainda espera o quadro (ver `destravar`).
 *
 * Fechar um modal e abrir outro no mesmo quadro — ou o StrictMode montando,
 * desmontando e remontando o efeito em dev — fazia o segundo `travar` ler
 * `scrollY` ainda em 0, antes da devolução acontecer. A página ia para o topo
 * e ficava lá. Com a devolução pendente, quem trava herda a posição dela.
 */
let devolucaoPendente: { alvo: number; quadro: number } | null = null;

const travar = (posicao?: number) => {
  if (abertos++ > 0) return;

  /*
   * `posicao` existe porque `window.scrollY` aqui pode já estar errado.
   *
   * O efeito roda DEPOIS que o React aplicou o DOM. Um modal que TIRA conteúdo
   * da página no mesmo commit — o de edição leva o formulário de lançamento
   * para dentro dele — encolhe o documento antes desta linha, e o navegador
   * grampeia a rolagem para a nova altura. Medido: 5112 virava 0.
   *
   * Quem sabe a posição certa é quem disparou a abertura, antes do estado
   * mudar. Sem esse caso, `window.scrollY` continua bastando.
   */
  if (devolucaoPendente) {
    cancelAnimationFrame(devolucaoPendente.quadro);
    scrollGuardado = posicao ?? devolucaoPendente.alvo;
    devolucaoPendente = null;
  } else {
    scrollGuardado = posicao ?? window.scrollY;
  }
  const barra = window.innerWidth - document.documentElement.clientWidth;

  const b = document.body.style;
  b.position = 'fixed';
  b.top = `-${scrollGuardado}px`;
  b.left = '0';
  b.right = '0';
  b.width = '100%';
  if (barra > 0) b.paddingRight = `${barra}px`;
};

const destravar = () => {
  if (--abertos > 0) return;
  if (abertos < 0) abertos = 0;

  const b = document.body.style;
  b.position = '';
  b.top = '';
  b.left = '';
  b.right = '';
  b.width = '';
  b.paddingRight = '';

  /*
   * A devolução espera um quadro, e o motivo é medido.
   *
   * Um modal pode ter TIRADO conteúdo da página — o de edição leva o
   * formulário de lançamento para dentro dele, e a seção fica com uma linha no
   * lugar. No instante em que este `cleanup` roda, a página ainda está curta:
   * pedir 5112 numa página que só tem 4869 devolve 4869, e a pessoa reaparece
   * 243px acima de onde estava.
   *
   * Um quadro depois o React já recolocou o conteúdo e a altura voltou. Sem
   * comportamento suave: devolver a posição é desfazer o travamento, não uma
   * navegação — animar mostraria um deslize que ninguém pediu.
   */
  const alvo = scrollGuardado;
  const quadro = requestAnimationFrame(() => {
    devolucaoPendente = null;
    window.scrollTo(0, alvo);
  });
  devolucaoPendente = { alvo, quadro };
};

/** Trava a rolagem do fundo enquanto o componente estiver montado. */
export const useTravarScroll = (voltarPara?: number): void => {
  useEffect(() => {
    travar(voltarPara);
    return destravar;
    // Só na montagem: mudar o alvo com o modal aberto moveria a página ao
    // fechar para um lugar que ela nunca esteve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/** Só para o teste: quantos modais estão segurando a página. */
export const _abertos = () => abertos;

/* ─── O foco do teclado ────────────────────────────────────────────────── */

/**
 * Enquanto o modal está aberto, o teclado só anda dentro dele.
 *
 * ── O que estava acontecendo ──────────────────────────────────────────────
 *
 * Medido com o modal do WhatsApp aberto: **106 dos 108 elementos focáveis da
 * página estavam FORA dele**, o fundo não tinha `inert` nem `aria-hidden`, e o
 * foco continuava onde estava — no botão do avatar, atrás da caixa.
 *
 * Na prática: quem navega por teclado abre o modal e o primeiro `Tab` leva
 * para trás dele, não para dentro. Quem usa leitor de tela ouve a página
 * inteira como se o modal não existisse. O `Esc` já fechava e a rolagem já
 * estava travada — faltava esta metade.
 *
 * ── Por que `inert` e não um laço de foco ────────────────────────────────
 *
 * O jeito clássico é escutar `Tab` e devolver o foco ao primeiro elemento
 * quando ele sai do último. Isso resolve o teclado e NÃO resolve o leitor de
 * tela, que navega por marcos e cabeçalhos sem passar por `Tab`. `inert`
 * resolve os dois de uma vez, porque tira o ramo inteiro da árvore de
 * acessibilidade — e é o próprio navegador quem faz, sem código para manter.
 *
 * ── Por que os IRMÃOS, e não o `#root` ───────────────────────────────────
 *
 * Marcar `#root` inteiro apagaria o modal junto, porque três dos cinco moram
 * dentro dele. Então o que fica inerte são os IRMÃOS do modal em cada nível,
 * subindo até o `body`: tudo menos o caminho que leva até a caixa. Funciona
 * igual para os que vão por portal, que são irmãos do `#root`.
 *
 * ── O foco volta para quem abriu ─────────────────────────────────────────
 *
 * Sem isso, fechar o modal joga o foco no `body` e o próximo `Tab` recomeça do
 * topo da página. Para quem abriu o WhatsApp pelo menu do avatar, é perder o
 * lugar na navegação por causa de uma caixa que já fechou.
 *
 * ── Um modal nunca apaga outro que está POR CIMA dele ────────────────────
 *
 * Quem abre depois não é sempre quem fica na frente. O Open Finance abre
 * sozinho na volta da conexão do banco, já no primeiro instante; o recomeço e
 * a pergunta do Finn abrem segundos depois, quando os dados chegam — e ficam
 * ATRÁS dele, que tem a camada mais alta. O de trás marcava o da frente como
 * irmão: o Open Finance continuava na tela e nenhum botão respondia (08/10,
 * com a primeira pessoa liberada). Por isso cada modal aberto entra numa
 * lista, e quem chega deixa em paz o irmão que guarda um modal de camada
 * mais alta que a sua.
 */
const modaisAbertos = new Set<HTMLElement>();

/** A camada em que o elemento é pintado: o `z-index` dele ou do ancestral mais próximo que tenha um. */
const camadaDe = (el: HTMLElement): number => {
  for (let n: HTMLElement | null = el; n && n !== document.body; n = n.parentElement) {
    const z = Number.parseInt(getComputedStyle(n).zIndex, 10);
    if (Number.isFinite(z)) return z;
  }
  return 0;
};

export const useFocoNoModal = (ref: RefObject<HTMLElement | null>): void => {
  const abriuCom = useRef<Element | null>(null);

  useEffect(() => {
    const caixa = ref.current;
    if (!caixa) return;

    abriuCom.current = document.activeElement;

    const minhaCamada = camadaDe(caixa);
    const guardaModalPorCima = (irmao: HTMLElement): boolean =>
      Array.from(modaisAbertos).some(m => irmao.contains(m) && camadaDe(m) > minhaCamada);
    modaisAbertos.add(caixa);

    const inertados: HTMLElement[] = [];
    let n: HTMLElement | null = caixa;
    while (n && n !== document.body) {
      const pai: HTMLElement | null = n.parentElement;
      if (!pai) break;
      const filhos: Element[] = Array.from(pai.children);
      for (const irmao of filhos) {
        if (irmao === n || !(irmao instanceof HTMLElement)) continue;
        if (irmao.hasAttribute('inert')) continue;   // já estava: não é nosso
        if (guardaModalPorCima(irmao)) continue;     // está na frente: é ele que a pessoa vê
        irmao.setAttribute('inert', '');
        inertados.push(irmao);
      }
      n = pai;
    }

    /*
     * O foco vai para o primeiro controle da caixa, e para a CAIXA quando ela
     * não tem nenhum. Focar o container exige `tabIndex={-1}`, então ele é
     * posto aqui e tirado na saída — pedir isso de cada modal seria mais uma
     * coisa para alguém esquecer.
     */
    const focavel = caixa.querySelector<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focavel) {
      focavel.focus();
    } else {
      caixa.setAttribute('tabindex', '-1');
      caixa.focus();
    }

    return () => {
      modaisAbertos.delete(caixa);
      for (const el of inertados) el.removeAttribute('inert');
      caixa.removeAttribute('tabindex');
      const volta = abriuCom.current;
      if (volta instanceof HTMLElement && document.contains(volta)) volta.focus();
    };
  }, [ref]);
};
